import { handleActions } from 'redux-actions';
import moment from 'moment';
import qs from 'qs';

import flatten from 'lodash/flatten';
import concat from 'lodash/concat';
import get from 'lodash/get';

import createCalendar, { getDateRangeWithTimeZoneFromCalendar } from '../../shared/calendar';
import createFilters, { getFiltersObject } from '../../shared/filters';
import http from '../../shared/http';

import { utmTagFilters } from '../../shared/filters/list';

import endpoints from '../../../config/endpoints';

export const namespace = 'marketingFunnel';

const calendar = createCalendar({
	namespace,
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().startOf('year').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace,
	stateNodeName: 'filters',
	listOfFilters: [
		{
			name: 'managers',
			title: 'Managers',
			placeholder: 'Add managers',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.ADMINS + '?role=manager'
			}
		},
		{
			name: 'tags',
			title: 'Tags',
			placeholder: 'Add affiliate\'s tag',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.AFFILIATE_TAGS
			}
		},
		{
			name: 'advertising_ids',
			title: 'Advertising IDs',
			placeholder: 'Add advertising IDs',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: `v2/marketing-funnel/activity/lookups/id`
			}
		},
		{
			name: 'advertising_types',
			title: 'Advertising types',
			placeholder: 'Add advertising types',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: `v2/marketing-funnel/activity/lookups/type`
			}
		}
	].concat(utmTagFilters)
});

// action types
const FETCH_DATA_SUCCESS = `${namespace}/fetch-data-success`;
const FETCH_DATA_ERROR = `${namespace}/fetch-data-error`;
const TOGGLE_PROGRESS_BAR = `${namespace}/toggle-progress-bar`;
const APPLY = `${namespace}/apply`;
const CLEAR = `${namespace}/clear`;
const CHANGE_KEY = `${namespace}/change-key`;

// initial state
const initialState = {
	isLoading: true,
	isError: false,
	data: null,
	name: 'registrations',
	filters: filters.initialState,
	calendar: calendar.initialState
};

// actions
export const actions = {
	own: {
		fetchData: () => (dispatch, getState) => {
			request(getRequestQuery(getState), FETCH_DATA_SUCCESS)(dispatch);
		},
		toggleProgressBar: () => ({ type: TOGGLE_PROGRESS_BAR }),
		createStatisticsQueryString: (affiliates) => (dispatch, getState) => {
			const range = getDateRangeWithTimeZoneFromCalendar(selectors.select(getState(), 'calendar'));

			return qs.stringify({
				entity: {
					filters: [
						{
							values: affiliates.map((a) => ({ value: a.id, label: a.email })),
							name: 'affiliates'
						}
					],
					groups: ['affiliate'],
					date_range: range
				},
				query: {
					filters: {
						affiliates: affiliates.map((a) => a.id)
					},
					groups: ['affiliate'],
					date_range: range
				}
			});
		},
		createAffiliateStatQueryString: (affiliates) => (dispatch, getState) => {
			const range = getDateRangeWithTimeZoneFromCalendar(selectors.select(getState(), 'calendar'));
			const queryString = qs.stringify({
				filters: {
					affiliates: affiliates.map((a) => ({ value: a.id, label: a.email }))
				},
				date_range: range
			});

			return queryString;
		},
		fetchDataByName: (name) => (dispatch, getState) => {
			dispatch(actions.own.toggleProgressBar());
			dispatch({ type: CHANGE_KEY, payload: { name } });
			dispatch(actions.own.fetchData());
			dispatch({ type: CHANGE_KEY, payload: { name: 'registrations' } });
		}
	},
	apply: () => (dispatch, getState) => {
		dispatch({ type: APPLY });
		dispatch(actions.own.fetchData());
	},
	clear: () => (dispatch, getState) => {
		dispatch({ type: CLEAR });
		dispatch(actions.own.fetchData());
	},
	filters: filters.actions,
	calendar: calendar.actions
};

const reducers = Object.assign(
	{
		[FETCH_DATA_SUCCESS]: (state, action) => {
			const months = getMonths(getDateRangeWithTimeZoneFromCalendar(state.calendar));

			const data = action.payload.data;

			function registrations(key) {
				return !get(data, key) ? get(state.data, key) || [] : get(data, key).map(({ month, affiliates }) => ({
					month,
					affiliates,
					approved_affiliates: affiliates.filter((a) => a.approved),
					activated_affiliates: affiliates.filter((a) => !!a.first_conversion_at)
				}));
			}
			function transitions(key) {
				return !get(data, key) ? get(state.data, key) || [] : get(data, key);
			}

			const all = {
				registrations: registrations('all.registrations'),
				transitions: transitions('all.transitions')
			};
			const conferences = {
				registrations: registrations('conferences.registrations'),
				transitions: transitions('conferences.transitions')
			};

			function prepareTotalAffiliates(entity, key) {
				return flatten(concat(entity.map((row) => row[key])));
			}

			const $total = {
				all: {
					registrations: {
						activated_affiliates: prepareTotalAffiliates(all.registrations, 'activated_affiliates'),
						approved_affiliates: prepareTotalAffiliates(all.registrations, 'approved_affiliates'),
						affiliates: prepareTotalAffiliates(all.registrations, 'affiliates')
					},
					transitions: {
						reactivated: prepareTotalAffiliates(all.transitions, 'reactivated'),
						affiliates: prepareTotalAffiliates(all.transitions, 'affiliates')
					}
				},
				conferences: {
					registrations: {
						activated_affiliates: prepareTotalAffiliates(conferences.registrations, 'activated_affiliates'),
						approved_affiliates: prepareTotalAffiliates(conferences.registrations, 'approved_affiliates'),
						affiliates: prepareTotalAffiliates(conferences.registrations, 'affiliates')
					},
					transitions: {
						reactivated: prepareTotalAffiliates(conferences.transitions, 'reactivated')
					}
				}
			};

			return {
				...state,
				data: {
					conferences,
					all,
					$total,
					months
				},
				isLoading: false,
				isError: false
			};
		},
		[FETCH_DATA_ERROR]: (state, action) => ({
			...state,
			isLoading: false,
			isError: true
		}),
		[TOGGLE_PROGRESS_BAR]: (state, action) => ({
			...state,
			isLoading: !state.isLoading
		}),
		[APPLY]: (state, action) => ({
			...state,
			data: null,
			isLoading: true
		}),
		[CLEAR]: (state, action) => initialState,
		[CHANGE_KEY]: (state, action) => ({
			...state,
			name: action.payload.name
		})
	},
	filters.reducers,
	calendar.reducers
);

// reducers
export default handleActions(reducers, initialState);

// selectors
export const selectors = {
	select: (state, field) => state[namespace][field],
	getFilters: state => filters.selectors.getFilters(
		state => state[namespace].filters,
		state => state
	)(state),
	getCalendar: state => calendar.selectors.getCalendar(
		state => state[namespace].calendar,
		state => state
	)(state)
};

function getRequestQuery(getState) {
	const state = getState()[namespace];

	const range = getDateRangeWithTimeZoneFromCalendar(state.calendar);
	const filters = getFiltersObject(state.filters, false);
	const inverse_filters = getFiltersObject(state.filters, true);

	return {
		dates: [range.start, range.end],
		key: state.name,
		filters,
		inverse_filters
	};
}

function request(query, successActionType) {
	return http({
		url: 'v2/marketing-funnel',
		method: 'get',
		actions: [successActionType, FETCH_DATA_ERROR],
		query
	});
}

function getMonths(range) {
	const start = moment(range.start).startOf('month');
	const end = moment(range.end).startOf('month');

	const n = end.diff(start, 'month');

	return Array.from({ length: n + 1 }, (_, i) => start.clone().add(i, 'month'));
}



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/marketing-funnel/main/index.js